/* eslint-disable @typescript-eslint/no-unused-vars */
import { createModel, RematchDispatch } from '@rematch/core';
import { toast } from 'react-toastify';

// Enums
import { AUTH_FLOW } from '../../enums/auth-step.enum';

// Types
import type { RootModel } from './index';

// Utils
import {
  cleanUserTokensFromLocalStorage,
  getAccessToken,
  getIsAuthenticated,
  getRefreshToken,
  setUserTokensToLocalStorage,
} from '../../utils/user';

// Constants
import URL from '../../constants/api-constants';

// Http
import http from '../http/http-common';
import { cleanOrganizationFromLocalStorage } from '../../utils/organization';
import { AxiosError } from 'axios';

interface IMfaCheckData {
  session: string;
}

interface IMfaSetupData extends IMfaCheckData {
  code: string;
  qrImage: string;
}

type CognitoToken = {
  access_token: string;
  refresh_token: string;
  session_token: string;
};

type AuthState = {
  authFlow: AUTH_FLOW;
  isAuthenticated: boolean;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthLoading: boolean;
  mfaSetupData: IMfaSetupData | null;
  mfaCheckData: IMfaCheckData | null;
};

const completeAuthentication = (dispatch: RematchDispatch<RootModel>, tokens: CognitoToken) => {
  const { access_token, refresh_token, session_token } = tokens;
  dispatch.auth.setIsAuthenticated({ isAuthenticated: true });
  dispatch.auth.setTokens({
    accessToken: access_token,
    refreshToken: refresh_token,
    sessionToken: session_token,
  });
  setUserTokensToLocalStorage(access_token, refresh_token, session_token);

  dispatch.auth.resetAuthState();
};

const handleAuthFlow = (dispatch: RematchDispatch<RootModel>, data: any, needChangeFlow: boolean) => {
  const { authFlow } = data;

  switch (authFlow) {
  case AUTH_FLOW.WITH_INVITATION_CODE:
  case AUTH_FLOW.FIRST_SIGN_IN:
    dispatch.auth.setAuthFlow({ authFlow });
    break;

  case AUTH_FLOW.MFA_SETUP: {
    const { authFlow: _, ...mfaSetupData } = data;
    dispatch.auth.setAuthFlow({ authFlow: AUTH_FLOW.MFA_SETUP });
    dispatch.auth.setMfaSetupData(mfaSetupData);
    break;
  }

  case AUTH_FLOW.MFA_CHECK: {
    const { authFlow: _, ...mfaCheckData } = data;
    if (needChangeFlow) {
      dispatch.auth.setAuthFlow({ authFlow: AUTH_FLOW.MFA_CHECK });
    }
    dispatch.auth.setMfaCheckData(mfaCheckData);
    break;
  }

  default:
    console.warn('Unknown auth flow:', authFlow);
  }

  return;
};

export const auth = createModel<RootModel>()({
  state: {
    authFlow: AUTH_FLOW.CHECK_EMAIL,
    isAuthenticated: getIsAuthenticated(),
    accessToken: getAccessToken(),
    refreshToken: getRefreshToken(),
    isAuthLoading: false,
    mfaCheckData: null,
    mfaSetupData: null,
  } as AuthState,
  reducers: {
    setIsAuthenticated(state, { isAuthenticated }) {
      return {
        ...state,
        isAuthenticated,
      };
    },

    setTokens(state, { accessToken }) {
      return {
        ...state,
        accessToken,
      };
    },

    setIsAuthLoading(state, isAuthLoading) {
      return {
        ...state,
        isAuthLoading,
      };
    },

    setAuthFlow(state, { authFlow }) {
      return {
        ...state,
        authFlow,
      };
    },

    setMfaCheckData(state, mfaCheckData) {
      return {
        ...state,
        mfaCheckData,
      };
    },

    setMfaSetupData(state, mfaSetupData) {
      return {
        ...state,
        mfaSetupData,
      };
    },

    resetAuthState(state) {
      return {
        ...state,
        mfaCheckData: null,
        mfaSetupData: null,
        authFlow: AUTH_FLOW.CHECK_EMAIL,
        isAuthLoading: false,
      };
    },
  },

  effects: (dispatch) => ({
    async checkAuthFlow(email) {
      try {
        dispatch.auth.setIsAuthLoading(true);

        const result = await http.post(URL.CHECK_AUTH_FLOW, {
          email,
        });

        dispatch.auth.setAuthFlow(result.data);
      } catch (error) {
        toast.error(error.message);
      } finally {
        dispatch.auth.setIsAuthLoading(false);
      }
    },

    async signIn({ email, password_digest, needChangeFlow = true }) {
      try {
        dispatch.auth.setIsAuthLoading(true);

        const response = await http.post(URL.SIGN_IN, { email, password_digest });
        const data = response.data;

        if (data?.authFlow) {
          handleAuthFlow(dispatch, data, needChangeFlow);
          return;
        }

        if (data?.access_token) {
          const tokens: CognitoToken = data;
          completeAuthentication(dispatch, tokens);
          return;
        }

        throw new Error('Authentication failed');
      } catch (error: unknown) {
        if (error instanceof AxiosError) {
          toast.error(error.response?.data?.message || 'An unexpected error occurred');
        } else if (error instanceof Error) {
          toast.error(error.message);
        } else {
          toast.error('An unexpected error occurred');
        }
        throw error;
      } finally {
        dispatch.auth.setIsAuthLoading(false);
      }
    },

    async setPermanentPassword({ email, password_digest }) {
      try {
        dispatch.auth.setIsAuthLoading(true);
        await http.post(URL.SET_PASSWORD, {
          email,
          password_digest,
        });

        await this.signIn({
          email,
          password_digest,
        });
      } catch (error) {
        toast.error(error.message);
      } finally {
        dispatch.auth.setIsAuthLoading(false);
      }
    },

    async verifyMfa({ session, code, email, password_digest }) {
      try {
        dispatch.auth.setIsAuthLoading(true);
        await http.post(URL.VERIFY_MFA_TOKEN, {
          session,
          email,
          code,
        });

        await this.signIn({ email, password_digest, needChangeFlow: false });
        dispatch.auth.setAuthFlow({
          authFlow: AUTH_FLOW.MFA_SETUP_COMPLETED,
        });
      } catch (error) {
        toast.error(error.message);
      } finally {
        dispatch.auth.setIsAuthLoading(false);
      }
    },

    async checkMfa({ session, code, email }) {
      try {
        dispatch.auth.setIsAuthLoading(true);
        const response = await http.post(URL.CHECK_MFA_CODE, {
          session,
          email,
          code,
        });

        if (response.data?.access_token) {
          const tokens: CognitoToken = response.data;
          completeAuthentication(dispatch, tokens);
          return;
        }

        throw new Error('Authentication failed');
      } catch (error) {
        toast.error(error.message);
      } finally {
        dispatch.auth.setIsAuthLoading(false);
      }
    },

    async redeemInvitationCode({ email, code }) {
      try {
        dispatch.auth.setIsAuthLoading(true);
        const result = await http.post(URL.REDEEM_INVITATION_CODE, {
          email,
          code,
        });

        dispatch.auth.setAuthFlow(result.data);
      } catch (error) {
        toast.error(error.message);
      } finally {
        dispatch.auth.setIsAuthLoading(false);
      }
    },

    logOutUser() {
      dispatch.auth.resetAuthState();
      dispatch.user.resetState();

      cleanUserTokensFromLocalStorage();
      cleanOrganizationFromLocalStorage();
      dispatch.auth.setIsAuthenticated({ isAuthenticated: false });
    },

    async signOut() {
      try {
        await http.get(URL.SIGN_OUT);

        this.logOutUser();
      } catch (error) {
        toast.error(error.message);
        console.error(error);
      }
    },

    async forgotPassword(email: string) {
      try {
        dispatch.auth.setIsAuthLoading(true);
        await http.post(URL.FORGOT_PASSWORD, {
          email,
        });
        toast.success('Password reset code has been sent to your email');
      } catch (error: unknown) {
        if (error instanceof AxiosError) {
          toast.error(error.response?.data?.message || 'Failed to initiate password reset');
        } else if (error instanceof Error) {
          toast.error(error.message);
        } else {
          toast.error('Failed to initiate password reset');
        }
        throw error;
      } finally {
        dispatch.auth.setIsAuthLoading(false);
      }
    },

    async confirmPasswordReset({ email, code, password }: { email: string; code: string; password: string }) {
      try {
        dispatch.auth.setIsAuthLoading(true);
        await http.post(URL.CONFIRM_PASSWORD_RESET, {
          email,
          code,
          password,
        });
        toast.success('Password has been reset successfully');
      } catch (error: unknown) {
        if (error instanceof AxiosError) {
          toast.error(error.response?.data?.message || 'Failed to reset password');
        } else if (error instanceof Error) {
          toast.error(error.message);
        } else {
          toast.error('Failed to reset password');
        }
        throw error;
      } finally {
        dispatch.auth.setIsAuthLoading(false);
      }
    },
  }),
});
